import api from 'services/api';
import { useCallback, useState } from 'react';
import { notification } from 'components/ui';
import { POST, PUT } from 'constants/api';
import { isEvent, isError } from 'helpers/simple';

const createUnifiedError = e => {
  const error = {
    message: e.message,
    status: e.status || e?.response?.status,
    statusText: e.statusText || e?.response?.statusText || e.message,
  };
  if (e?.response?.data?.error?.message)
    error.message = e.response.data.error.message;
  if (e?.response?.data?.message) error.message = e.response.data.message;

  const unifiedError = new Error(error.message);
  unifiedError.status = error.status;
  unifiedError.statusText = error.statusText;

  return unifiedError;
};

const getToastMessage = (data, message) => {
  if (typeof message === 'function') return message(data);
  return message;
};

const getDefaultToastMessage = ({ isUpdate, isErr }) => {
  if (isUpdate) {
    if (isErr) return 'Failed to update';
    return 'Updated successfully';
  }
  if (isErr) return 'Failed to create';
  return 'Success';
};

const createToast = (type, { data, message, description, isUpdate }) => {
  const notifier = notification[type] || notification.info;
  const msg = getToastMessage(data, message);
  const des = getToastMessage(data, description);
  notifier({
    message:
      msg || getDefaultToastMessage({ isUpdate, isErr: type === 'error' }),
    description: des,
  });
};

const useMutationIndicator = (
  mutator,
  {
    toastOptions,
    transformRes,
    onSuccess: onSuccess2,
    onError: onError2,
    isUpdate,
  },
) => {
  const {
    errorMsg,
    errorDes,
    successMsg,
    successDes,
    showToast,
  } = toastOptions;
  const [indicator, setIndicator] = useState({ loading: false, error: null });
  const setError = useCallback(
    error =>
      setIndicator({
        error: isError(error) ? error : new Error(error),
      }),
    [indicator.error],
  );
  const doMutation = async (body, { onSuccess, onError } = {}) => {
    try {
      setIndicator({ loading: true, error: null });
      const res = await mutator(isEvent(body) ? undefined : body);
      const data = transformRes ? transformRes(res) : res;
      setIndicator({ loading: false, error: null });
      if (onSuccess2) await onSuccess2(data, { setError });
      if (onSuccess) await onSuccess(data, { setError });
      if (showToast)
        createToast('success', {
          data,
          message: successMsg,
          description: successDes,
          isUpdate,
        });
      return data;
    } catch (e) {
      setIndicator({ loading: false, error: e });
      if (showToast)
        createToast('error', {
          data: createUnifiedError(e),
          message: errorMsg,
          description: errorDes,
          isUpdate,
        });
      if (onError2) await onError2(e);
      if (onError) await onError(e);
    }
  };
  return [doMutation, indicator];
};

const useUpdatePattern = ({ axiosOptions, ...options }) => {
  const mutator = body => api({ ...axiosOptions, data: body, method: PUT });
  return useMutationIndicator(mutator, { ...options, isUpdate: true });
};

const useCreatePattern = ({ axiosOptions, ...options }) => {
  const mutator = body => api({ ...axiosOptions, data: body, method: POST });
  return useMutationIndicator(mutator, { ...options });
};

export const useUpdate = (
  url,
  id,
  {
    axiosOptions,
    transformRes,
    onSuccess,
    onError,
    errorMsg,
    errorDes,
    successMsg,
    successDes,
    showToast = true,
  } = {},
) =>
  useUpdatePattern({
    onSuccess,
    onError,
    transformRes,
    axiosOptions: { ...axiosOptions, params: { id }, url },
    toastOptions: { errorMsg, errorDes, successDes, successMsg, showToast },
  });

export const useCreate = (
  url,
  {
    axiosOptions,
    transformRes,
    onSuccess,
    onError,
    errorMsg,
    errorDes,
    successMsg,
    successDes,
    showToast = true,
  } = {},
) =>
  useCreatePattern({
    onSuccess,
    onError,
    transformRes,
    axiosOptions: { ...axiosOptions, url },
    toastOptions: { errorMsg, errorDes, successDes, successMsg, showToast },
  });
